import { GENERIC_FILTER_TEMPLATE_DETAILS_QUERY, PREDEFINED_GENERIC_FILTER_TEMPLATES } from 'features/genericFilterTemplates/edit/queries';
import { GENERIC_FILTER_PARAM_QUERY } from 'features/genericFilters/edit/queries';
import {
  GENERIC_FILTER_OPERATORS_QUERY,
  GENERIC_FILTER_PARAMS_QUERY,
  GENERIC_FILTER_PARAM_VALUES_QUERY,
  PAGED_GENERIC_FILTER_PARAM_VALUES_QUERY
} from 'features/genericFilters/queries';
import { useQuery, useClientQuery } from 'hooks/apollo';
import { useCallback, useState } from 'react';

export const GenericFilterContext = {
  InsuranceConvention: 'CONVENTION_FILTER',
  FinancialProduct: 'FP_FILTER',
  FinancialService: 'FINANCIAL_SERVICE',
  DocumentTemplate: 'DOC_TEMPLATE',
  RequiredDocument: 'RD_FILTER'
};

const useGenericFilterData = (options = {}) => {
  const { context, withTemplateSelection } = options;

  const clientQuery = useClientQuery();

  // #region Data

  const [parameters, setParameters] = useState([]);
  const [operators, setOperators] = useState([]);
  const [templates, setTemplates] = useState([]);

  useQuery(PREDEFINED_GENERIC_FILTER_TEMPLATES, {
    skip: !withTemplateSelection,
    onCompleted: data => {
      setTemplates(data?.predefinedGenericFilterTemplates || []);
    }
  });

  useQuery(GENERIC_FILTER_PARAMS_QUERY, {
    variables: { context },
    onCompleted: data => {
      setParameters(data?.genericFilterParams || []);
    }
  });

  useQuery(GENERIC_FILTER_OPERATORS_QUERY, {
    onCompleted: data => {
      setOperators(data?.genericFilterOperators || []);
    }
  });

  const loadValues = useCallback(
    async paramId => {
      if (!paramId) return [];

      const { data } = await clientQuery(GENERIC_FILTER_PARAM_VALUES_QUERY, {
        variables: { paramId: parseInt(paramId) }
      });

      const values = data?.genericFilterParamValues.map(z => ({ value: z.id, label: z.name }));
      return values || [];
    },
    [clientQuery]
  );

  const loadPagedValues = useCallback(
    paramId => async (name, _, additional) => {
      const page = additional?.page ?? 1;
      const { data } = await clientQuery(PAGED_GENERIC_FILTER_PARAM_VALUES_QUERY, {
        variables: { paramId: parseInt(paramId), name, page },
        skip: !paramId
      });
      const values = data?.pagedGenericFilterParamValues.map(z => ({ value: z.id, label: z.name }));
      const options = values || [];

      return {
        loadedOptions: options,
        more: options.length > 0,
        additional: { page: page + 1 }
      };
    },
    [clientQuery]
  );

  const loadMissingParamters = useCallback(
    conditions => {
      const missingParameterIds = (conditions || [])
        .filter(condition => !parameters.some(parameter => parameter.paramId === condition.paramId))
        .map(x => x.paramId);

      const parameterQueries = missingParameterIds.map(paramId =>
        clientQuery(GENERIC_FILTER_PARAM_QUERY, {
          variables: { paramId }
        })
      );

      Promise.allSettled(parameterQueries).then(results => {
        const newParamters = [];
        results.forEach(result => {
          const parameter = result?.value?.data?.genericFilterParam;
          if (!parameter) return;
          newParamters.push(parameter);
        });
        setParameters(prev => [...prev, ...newParamters]);
      });
    },
    [clientQuery, parameters]
  );

  const loadTemplate = useCallback(
    async id => {
      if (!id) return null;

      const { data } = await clientQuery(GENERIC_FILTER_TEMPLATE_DETAILS_QUERY, {
        variables: { id }
      });

      // The template might come with parameters that haven't been loaded (because of the context restriction)
      // This means their name won't be displayed, so they need to be loaded separately.
      loadMissingParamters(data?.genericFilterTemplateDetails?.conditions);

      return data?.genericFilterTemplateDetails || null;
    },
    [clientQuery, loadMissingParamters]
  );

  // #endregion

  return { templates, parameters, operators, loadValues, loadPagedValues, loadMissingParamters, loadTemplate };
};

export default useGenericFilterData;
